import { forwardRef, SyntheticEvent, useImperativeHandle, useRef, useState } from 'react';
import { OverlayPanel } from 'primereact/overlaypanel';
import { BlockOtherUnitsSwitch } from './BlockOtherUnitsSwitch';
import { useUnitFocus } from '../../custom-hooks/useUnitFocus';
import { resolveFocusRestrictDefault } from '../../businessLogicService/focusService';
import { useUnitFocusEvent } from '../../custom-hooks/useUnitFocusEvent';

interface Props {
  /** The unit whose active focus is being edited. */
  targetId: string;
  /** The roadmap the edited unit belongs to. */
  roadmapId: string;
  /** Classes sharing the edited focus. */
  classIds: readonly string[];
  /** Fires with the panel's visibility so the trigger button can mirror the open state. */
  onVisibleChange?: (visible: boolean) => void;
}

export interface EditFocusSettingsPopoverHandle {
  toggle: (e: SyntheticEvent) => void;
}

export const EditFocusSettingsPopover = forwardRef<EditFocusSettingsPopoverHandle, Props>(
  function EditFocusSettingsPopover({ targetId, roadmapId, classIds, onVisibleChange }, ref) {
    const op = useRef<OverlayPanel>(null);
    useImperativeHandle(ref, () => ({ toggle: (e: SyntheticEvent) => op.current?.toggle(e) }), []);
    const captureUnitFocusEvent = useUnitFocusEvent();
    const { focuses, updateFocusSettings } = useUnitFocus();
    // Falls back to blocked (the create popover's default) when the rows haven't loaded yet.
    const persistedRestrict = resolveFocusRestrictDefault(focuses, targetId);
    const [restrict, setRestrict] = useState(persistedRestrict);

    // Flip the switch immediately, persist behind it, and roll back if the backend refuses.
    const handleToggle = (next: boolean) => {
      setRestrict(next);
      updateFocusSettings(targetId, next ? 'restrict' : 'recommend').catch(() => setRestrict(!next));

      captureUnitFocusEvent(
        'unit_focus_unitblocking_updated',
        { classIds, targetId, targetGroupId: roadmapId, blockingEnabled: next },
        { blocking_choice: next ? 'chose_on' : 'chose_off' },
      );
    };

    return (
      <OverlayPanel
        ref={op}
        onShow={() => {
          setRestrict(persistedRestrict);
          onVisibleChange?.(true);
        }}
        onHide={() => onVisibleChange?.(false)}
        className="!tw-w-96 !tw-rounded-2xl !tw-border-2 !tw-border-b-4 !tw-border-neutral-300 !tw-shadow-[0px_0px_15px_rgba(132,123,111,0.2)]"
        pt={{ content: { className: 'p-0' } }}
      >
        <div className="tw-border-b-2 tw-border-neutral-100 tw-p-4">
          <span className="tw-font-atkinson tw-text-xs tw-font-bold tw-uppercase tw-leading-4 tw-text-neutral-600">
            Edit settings
          </span>
        </div>
        <div className="tw-p-4">
          <BlockOtherUnitsSwitch
            checked={restrict}
            onChange={handleToggle}
            title="Other units blocked"
            description="Students can only work in the focused Unit"
            showSectionLabel={false}
          />
        </div>
      </OverlayPanel>
    );
  },
);
